/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { NodesApi } from '@alfresco/js-api';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AcaConfigFolderService } from '../shared/aca-config-folder.service';
import { FlowGraph, isFlowKey } from './flow-graph';

export interface ProcessDefinition {
  id: string;
  key: string;
  name: string;
}

export interface WorkflowTask {
  id: string;
  processId: string;
  name: string;
  description?: string;
  assignee?: string;
  state?: string;
  priority?: number;
  startedAt?: string;
  endedAt?: string;
  dueAt?: string;
  formResourceKey?: string;
}

export interface WorkflowProcess {
  id: string;
  processDefinitionKey: string;
  processDefinitionId?: string;
  startUserId?: string;
  startedAt?: string;
  endedAt?: string;
  completed?: boolean;
}

export interface PersonRef {
  id: string;
  displayName: string;
}

/** An item (file/folder node) attached to a process or task. */
export interface WorkflowItem {
  id: string;
  name: string;
  title?: string;
  modifiedAt?: string;
  createdAt?: string;
  mimeType?: string;
  size?: number;
}

/** A required property the initiator must fill in when starting a step. */
export interface RequestProperty {
  name: string;
  label: string;
  required: boolean;
}

/**
 * Optional gate a step carries — when it fails the step is skipped and the
 * flow continues to the next runnable step, the same way a routed branch in
 * a visual builder behaves.
 */
export interface StepCondition {
  /** Key into the instance's stored values — e.g. a `dms:` property name. */
  field: string;
  op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'empty' | 'notEmpty';
  value?: string;
}

export interface WorkflowStep {
  id: string;
  name: string;
  assigneeType: 'person' | 'group';
  assignee: string; // userId or groupId
  assigneeLabel?: string;
  type: 'review' | 'task'; // review = approve/reject, task = adhoc to-do
  properties: RequestProperty[];
  /** Run this step only when the condition holds against the instance's values. */
  condition?: StepCondition;
  /** Step index a rejection returns the flow to; unset means the flow ends returned. */
  onRejectStep?: number;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  steps: WorkflowStep[];
  /** Management metadata — additive; older stored templates simply lack them. */
  version?: number;
  enabled?: boolean;
  category?: string;
  slaDays?: number;
  updatedAt?: string;
  updatedBy?: string;
  /** Snapshots of earlier saves, newest last — the running list can point at one. */
  versions?: WorkflowTemplate[];
  /**
   * Visibility gate — when set, only the listed people/groups (and
   * administrators) may see and start this flow. Absent means everyone.
   */
  restricted?: boolean;
  allowedUsers?: PersonRef[];
  allowedGroups?: PersonRef[];
  /**
   * The flow drawn as a graph. A template with one runs as a real process
   * definition once published; one without is the older step list, which the
   * browser runs a step at a time.
   */
  graph?: FlowGraph;
  /** The last publish of the graph to the process engine. */
  deployment?: FlowDeployment;
}

/** Where a graph template's process definition lives, and which template version it was. */
export interface FlowDeployment {
  /** Process definition key — {@link processKey} of the template id. */
  key: string;
  /** The bpm:workflowDefinition node in Data Dictionary/Workflow Definitions. */
  nodeId: string;
  /** The engine's id for the deployed version, `key:version:id`. */
  definitionId: string;
  /** The template version that was published. */
  templateVersion: number;
  deployedAt: string;
  deployedBy?: string;
}

/** legacy: a step list · draft: drawn, never published · changed: edited since its publish · published. */
export type FlowState = 'legacy' | 'draft' | 'changed' | 'published';

/** Who is looking, distilled to what visibility matching needs. */
export interface ViewerContext {
  id: string;
  isAdmin: boolean;
  /** Alfresco group ids (`GROUP_*`). */
  groups: Set<string>;
}

/** A template flow just started: its first process, and whether the step chain was recorded (without it, later steps do not follow). */
export interface LaunchedFlow {
  process: WorkflowProcess;
  recorded: boolean;
}

/** Why a template could not start: nobody is on the step it would open with. */
export const NO_ASSIGNEE = 'NO_ASSIGNEE';

/** One decision on a template step. `by` is stamped by the server from the caller. */
export interface InstanceHistoryEntry {
  step: number;
  name: string;
  outcome: string;
  at: string;
  by?: string;
  comment?: string;
}

/**
 * A running instance of a multi-step template. Each step is its own Activiti
 * process; this record carries the chain between them and is kept by the jobs
 * sidecar, open to whoever Alfresco shows one of its processes to.
 */
export interface TemplateInstance {
  id: string;
  templateId: string;
  templateName: string;
  /** Version of the template this instance runs against. */
  templateVersion?: number;
  /** Node ids attached when the instance started — shown on the tracking page. */
  nodeIds?: string[];
  currentStep: number;
  status: 'running' | 'completed' | 'returned' | 'cancelled';
  taskId?: string;
  processId?: string;
  /** Every process the flow has run, oldest first — filled in by the server. */
  processIds?: string[];
  /** Who started it — filled in by the server. */
  startedBy?: string;
  startedAt: string;
  history: InstanceHistoryEntry[];
  /** Document/process values the step conditions evaluate against. */
  vars?: Record<string, any>;
}

const TEMPLATES_KEY = 'aca.wf.templates';
const INSTANCES_KEY = 'aca.wf.instances';

/**
 * Talks to the Alfresco (Activiti) Workflow public REST API and the Core API,
 * using the logged-in user's ECM ticket as HTTP Basic credentials.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowService {
  private readonly api = inject(AlfrescoApiService);
  private readonly configFolder = inject(AcaConfigFolderService);
  private readonly translate = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthenticationService);
  private cachedUser = '';

  /** Workflow public API — discoveryClient.basePath is `{ecm}/alfresco/api`, so auth is handled for us. */
  private wfCall(path: string, method: string, body?: any, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.discoveryClient.callApi(
        `/-default-/public/workflow/versions/1${path}`,
        method,
        {},
        query ?? {},
        {},
        {},
        body ?? null,
        ['application/json'],
        ['application/json']
      ) as Promise<any>
    );
  }

  /** Core API — contentClient.basePath is `{ecm}/.../public/alfresco/versions/1`. */
  private coreCall(path: string, method: string, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>);
  }

  /** The signed-in person's id — cached for the session. */
  currentUserId(): Observable<string> {
    return this.resolveUser();
  }

  private resolveUser(): Observable<string> {
    if (this.cachedUser) {
      return of(this.cachedUser);
    }
    return this.coreCall('/people/-me-', 'GET').pipe(
      map((r: any) => {
        this.cachedUser = r?.entry?.id || '';
        return this.cachedUser;
      })
    );
  }

  // ---- Process definitions -------------------------------------------------

  /**
   * Definition keys the Alfresco repository ships out of the box. They stay
   * deployable — template steps still start `activitiReview` under the hood —
   * but the start wizard hides them: the page is for flows the administrator
   * designed, not the stock definitions nobody maintains here.
   */
  private static readonly BUILTIN_DEFINITION_KEYS = new Set([
    'activitiAdhoc',
    'activitiReview',
    'activitiReviewPooled',
    'activitiParallelReview',
    'activitiParallelGroupReview',
    'activitiReviewAndApprove',
    'activitiInvitationNominated',
    'activitiInvitationModerated'
  ]);

  /** Whether a process-definition is one Alfresco shipped, not one deployed here. */
  static isBuiltinDefinition(def: ProcessDefinition): boolean {
    return WorkflowService.BUILTIN_DEFINITION_KEYS.has(def?.key);
  }

  /**
   * Published process-definitions. Built-in Alfresco definitions are left out
   * unless `includeBuiltin` is set — admin dashboards pass it so their KPI
   * still counts everything the engine deployed.
   */
  listProcessDefinitions(includeBuiltin = false): Observable<ProcessDefinition[]> {
    return this.wfCall('/process-definitions', 'GET', null, { maxItems: 100 }).pipe(
      map((r) =>
        (r?.list?.entries ?? [])
          .map((e: any) => e.entry as ProcessDefinition)
          .filter((d: ProcessDefinition) => includeBuiltin || !WorkflowService.isBuiltinDefinition(d))
          // a published graph is listed as its template, not a second time as a bare definition
          .filter((d: ProcessDefinition) => includeBuiltin || !isFlowKey(d.key))
      )
    );
  }

  /** Where a template stands between drawing and running. */
  static flowState(tpl: WorkflowTemplate): FlowState {
    if (!tpl.graph) {
      return 'legacy';
    }
    if (!tpl.deployment) {
      return 'draft';
    }
    return tpl.deployment.templateVersion === (tpl.version ?? 1) ? 'published' : 'changed';
  }

  /**
   * Records a publish on the template without counting it as an edit — the
   * version it names is the one just saved.
   */
  setDeployment(id: string, deployment: FlowDeployment): Observable<void> {
    const all = [...this.rawTemplates()];
    const i = all.findIndex((t) => t.id === id);
    if (i < 0) {
      return of(undefined);
    }
    all[i] = { ...all[i], deployment };
    return this.persistTemplates(all);
  }

  // ---- Processes -----------------------------------------------------------
  startProcess(processDefinitionKey: string, variables: Record<string, any>, items: string[] = []): Observable<WorkflowProcess> {
    const body: any = { processDefinitionKey, variables };
    if (items.length) {
      body.items = items;
    }
    return this.wfCall('/processes', 'POST', body).pipe(map((r) => r.entry as WorkflowProcess));
  }

  getProcess(id: string): Observable<WorkflowProcess> {
    return this.wfCall(`/processes/${id}`, 'GET').pipe(map((r) => r.entry as WorkflowProcess));
  }

  /** Process variables — description, due date, priority, outcome values. */
  processVariables(id: string): Observable<Record<string, any>> {
    return this.wfCall(`/processes/${id}/variables`, 'GET').pipe(
      map((r) => {
        const vars: Record<string, any> = {};
        for (const v of r?.list?.entries ?? []) {
          vars[v.entry.name] = v.entry.value;
        }
        return vars;
      }),
      catchError(() => of({}))
    );
  }

  deleteProcess(id: string): Observable<void> {
    return this.wfCall(`/processes/${id}`, 'DELETE');
  }

  // ---- Tasks ---------------------------------------------------------------
  processTasks(processId: string): Observable<WorkflowTask[]> {
    return this.wfCall(`/processes/${processId}/tasks`, 'GET').pipe(map((r) => (r?.list?.entries ?? []).map((e: any) => e.entry as WorkflowTask)));
  }

  /**
   * Tasks belonging to a process in any state — the tracking timeline's source.
   * `/processes/{id}/tasks` only ever returns active tasks, so completed ones
   * are merged in from the global task endpoint which accepts a status filter.
   */
  processTasksAll(processId: string): Observable<WorkflowTask[]> {
    const entries = (r: any): WorkflowTask[] => (r?.list?.entries ?? []).map((e: any) => e.entry as WorkflowTask);
    const active$ = this.wfCall(`/processes/${processId}/tasks`, 'GET', null, { maxItems: 100 }).pipe(
      map(entries),
      catchError(() => of([] as WorkflowTask[]))
    );
    const done$ = this.wfCall('/tasks', 'GET', null, { where: `(processId='${processId}' AND status='completed')`, maxItems: 100 }).pipe(
      map(entries),
      catchError(() => of([] as WorkflowTask[]))
    );
    return forkJoin([active$, done$]).pipe(map(([active, done]) => [...active, ...done]));
  }

  // ---- Attached items (files linked to the flow) ---------------------------
  processItems(processId: string): Observable<WorkflowItem[]> {
    return this.wfCall(`/processes/${processId}/items`, 'GET').pipe(
      map((r) => (r?.list?.entries ?? []).map((e: any) => e.entry as WorkflowItem)),
      catchError(() => of([] as WorkflowItem[]))
    );
  }

  taskItems(taskId: string): Observable<WorkflowItem[]> {
    return this.wfCall(`/tasks/${taskId}/items`, 'GET').pipe(
      map((r) => (r?.list?.entries ?? []).map((e: any) => e.entry as WorkflowItem)),
      catchError(() => of([] as WorkflowItem[]))
    );
  }

  /** Links an existing repository node to the task as an extra attachment. */
  /**
   * The outcomes a task's form offers: the allowed values of its
   * `wf:reviewOutcome` field, `[]` for a form without one, null when the form
   * model could not be read (the caller then falls back to the definition name).
   */
  taskOutcomes(taskId: string): Observable<string[] | null> {
    return this.wfCall(`/tasks/${taskId}/task-form-model`, 'GET').pipe(
      map((r) => {
        const fields = (r?.list?.entries ?? []).map((e: any) => e.entry);
        const outcome = fields.find((f: any) => /reviewOutcome$/.test(String(f?.qualifiedName ?? f?.name ?? '')));
        return outcome ? ((outcome.allowedValues ?? []) as string[]) : [];
      }),
      catchError(() => of(null))
    );
  }

  addTaskItem(taskId: string, nodeId: string): Observable<WorkflowItem> {
    return this.wfCall(`/tasks/${taskId}/items`, 'POST', { id: nodeId }).pipe(map((r) => r?.entry as WorkflowItem));
  }

  /**
   * Complete a task; for review tasks pass outcome 'Approve' | 'Reject'.
   * A comment rides along as `bpm_comment` — the property Activiti's own
   * review form writes, so the history shows what the reviewer said.
   */
  completeTask(taskId: string, outcome?: 'Approve' | 'Reject', comment?: string): Observable<any> {
    const finish = () => this.wfCall(`/tasks/${taskId}`, 'PUT', { state: 'completed' }, { select: 'state' });

    const vars: { name: string; value: string; scope: string; type: string }[] = [];
    if (outcome) {
      vars.push({ name: 'wf_reviewOutcome', value: outcome, scope: 'local', type: 'd:text' });
    }
    if (comment?.trim()) {
      vars.push({ name: 'bpm_comment', value: comment.trim(), scope: 'local', type: 'd:text' });
    }
    if (!vars.length) {
      return finish();
    }
    return this.wfCall(`/tasks/${taskId}/variables`, 'POST', vars).pipe(switchMap(() => finish()));
  }

  reassignTask(taskId: string, assignee: string): Observable<any> {
    return this.wfCall(`/tasks/${taskId}`, 'PUT', { assignee }, { select: 'assignee' });
  }

  claimTask(taskId: string): Observable<any> {
    return this.wfCall(`/tasks/${taskId}`, 'PUT', { state: 'claimed' }, { select: 'state' });
  }

  /** Hand a claimed task back to its candidate group. */
  releaseTask(taskId: string): Observable<any> {
    return this.wfCall(`/tasks/${taskId}`, 'PUT', { state: 'unclaimed' }, { select: 'state' });
  }

  // ---- People & groups (approver pickers) ----------------------------------
  searchPeople(term: string): Observable<PersonRef[]> {
    if (!term || term.length < 1) {
      return of([]);
    }
    return this.coreCall('/queries/people', 'GET', { term, maxItems: 10 }).pipe(
      map((r) => (r?.list?.entries ?? []).map((e: any) => ({ id: e.entry.id, displayName: e.entry.displayName || e.entry.id })))
    );
  }

  searchGroups(term: string): Observable<PersonRef[]> {
    return this.coreCall('/groups', 'GET', { maxItems: 100 }).pipe(
      map((r) =>
        (r?.list?.entries ?? [])
          .map((e: any) => ({ id: e.entry.id, displayName: e.entry.displayName || e.entry.id }))
          .filter((g: PersonRef) => !term || g.displayName.toLowerCase().includes(term.toLowerCase()))
          .slice(0, 15)
      )
    );
  }

  // ---- Builder templates & instances (localStorage) ------------------------

  /**
   * Three worked examples, seeded the first time the builder is opened.
   *
   * They exist so the screen has something real to open, run and edit rather
   * than an empty list, and so the three shapes the builder supports are each
   * represented: a single approver, a chain of two, and a group review followed
   * by an acknowledgement. Assignees are left blank on purpose — a template
   * cannot run until someone in this repository is chosen for its first step,
   * and inventing a user name here would only fail at the point of use.
   *
   * Once seeded they are ordinary templates: editable, runnable and deletable,
   * and deleting them does not bring them back.
   */
  private static readonly SAMPLE_TEMPLATES: WorkflowTemplate[] = [
    {
      id: 'tpl-sample-single-approval',
      name: 'DMS.UI.7A466E',
      description: 'DMS.UI.AD06EA',
      steps: [
        {
          id: 'tpl-sample-single-approval-1',
          name: 'DMS.UI.2AF9A8',
          assigneeType: 'person',
          assignee: '',
          assigneeLabel: '',
          type: 'review',
          properties: [{ name: 'comment', label: 'DMS.UI.CCC47B', required: false }]
        }
      ]
    },
    {
      id: 'tpl-sample-two-step',
      name: 'DMS.UI.0EFDFE',
      description: 'DMS.UI.948950',
      steps: [
        {
          id: 'tpl-sample-two-step-1',
          name: 'DMS.UI.C35B5E',
          assigneeType: 'person',
          assignee: '',
          assigneeLabel: '',
          type: 'review',
          properties: [{ name: 'checkedPoints', label: 'DMS.UI.8E6281', required: true }]
        },
        {
          id: 'tpl-sample-two-step-2',
          name: 'DMS.UI.59A127',
          assigneeType: 'person',
          assignee: '',
          assigneeLabel: '',
          type: 'review',
          properties: [{ name: 'comment', label: 'DMS.UI.CCC47B', required: false }]
        }
      ]
    },
    {
      id: 'tpl-sample-group-review',
      name: 'DMS.UI.E9E4AD',
      description: 'DMS.UI.700D18',
      steps: [
        {
          id: 'tpl-sample-group-review-1',
          name: 'DMS.UI.0E0BB4',
          assigneeType: 'group',
          assignee: '',
          assigneeLabel: '',
          type: 'review',
          properties: [{ name: 'resolution', label: 'DMS.UI.286D9F', required: true }]
        },
        {
          id: 'tpl-sample-group-review-2',
          name: 'DMS.UI.282F21',
          assigneeType: 'person',
          assignee: '',
          assigneeLabel: '',
          type: 'task',
          properties: []
        }
      ]
    }
  ];

  /** What a value written by the seed rather than by a person looks like. */
  private static readonly KEY_SHAPE = /^DMS\.[A-Z0-9_]+\.[A-Za-z0-9_]+$/;

  private text(value: string): string {
    return WorkflowService.KEY_SHAPE.test(value) ? this.translate.instant(value) : value;
  }

  /**
   * Puts the worked examples into the reader's own language. Everything a
   * person types is left exactly as typed — only the seed's own placeholders
   * are resolved — so this also repairs a browser seeded before the samples
   * were translated at all.
   */
  private resolve(templates: WorkflowTemplate[]): WorkflowTemplate[] {
    return templates.map((tpl) => ({
      ...tpl,
      name: this.text(tpl.name),
      description: tpl.description ? this.text(tpl.description) : tpl.description,
      steps: (tpl.steps ?? []).map((step) => ({
        ...step,
        name: this.text(step.name),
        properties: (step.properties ?? []).map((prop) => ({ ...prop, label: this.text(prop.label) }))
      }))
    }));
  }

  /**
   * Templates as currently known. The catalogue actually lives in the
   * repository — `Data Dictionary/ACA/workflow-templates.json`, the same
   * place menu permissions keep their policy document — so a flow the
   * administrator draws in settings is visible to every signed-in user,
   * subject to its own visibility gate. This method stays synchronous:
   * callers that need guaranteed-fresh data go through
   * {@link ensureTemplatesLoaded} first, which warms the cache this reads.
   * Before any load, it falls back to this browser's local copy — the store
   * the templates grew up in.
   */
  getTemplates(): WorkflowTemplate[] {
    return this.resolve(this.tplCache ?? this.localTemplates());
  }

  /** This browser's local copy — the original store, now an offline fallback. */
  private localTemplates(): WorkflowTemplate[] {
    const stored = localStorage.getItem(TEMPLATES_KEY);
    if (stored === null) {
      // first visit: lay down the worked examples raw — keys intact, so each
      // reader sees them in their own language after resolve()
      localStorage.setItem(TEMPLATES_KEY, JSON.stringify(WorkflowService.SAMPLE_TEMPLATES));
      return WorkflowService.SAMPLE_TEMPLATES;
    }
    return JSON.parse(stored);
  }

  /** Raw template records — shared cache when loaded, else the local copy. */
  private rawTemplates(): WorkflowTemplate[] {
    return this.tplCache ?? this.localTemplates();
  }

  /**
   * Loads the shared catalogue once per session. When the repository has no
   * document yet, an administrator's visit publishes this browser's copy —
   * custom flows included — so existing work is promoted, not lost.
   */
  ensureTemplatesLoaded(): Observable<WorkflowTemplate[]> {
    if (this.tplCache) {
      return of(this.tplCache);
    }
    return this.readSharedTemplates().pipe(
      switchMap((saved) => {
        if (saved) {
          this.tplCache = saved;
          return of(saved);
        }
        const local = this.localTemplates();
        this.tplCache = local;
        return this.viewerContext().pipe(
          switchMap((v) =>
            v.isAdmin
              ? this.writeSharedTemplates(local).pipe(
                  map(() => local),
                  catchError(() => of(local))
                )
              : of(local)
          )
        );
      }),
      catchError(() => {
        this.tplCache = this.localTemplates();
        return of(this.tplCache);
      })
    );
  }

  /**
   * Persists the whole list for everyone — cache, this browser's mirror and
   * the repository document. Requires an administrator; the builder and the
   * management catalogue sit behind the admin guard already.
   */
  private persistTemplates(all: WorkflowTemplate[]): Observable<void> {
    this.tplCache = all;
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(all));
    return this.writeSharedTemplates(all);
  }

  /**
   * Persist a template. Re-saving an existing id pushes the previous record
   * onto `versions` and bumps `version`, so instances started earlier keep
   * the definition they ran against — the same rule a process engine applies
   * to a new process-definition version.
   */
  saveTemplate(tpl: WorkflowTemplate): Observable<void> {
    const all = [...this.rawTemplates()];
    const i = all.findIndex((t) => t.id === tpl.id);
    const next: WorkflowTemplate = { ...tpl, updatedAt: new Date().toISOString() };
    if (i >= 0) {
      const { versions = [], ...snapshot } = all[i];
      next.version = (all[i].version ?? 1) + 1;
      next.versions = [...versions, snapshot];
      all[i] = next;
    } else {
      next.version = next.version ?? 1;
      next.enabled = next.enabled ?? true;
      all.push(next);
    }
    return this.persistTemplates(all);
  }

  /**
   * Start-wizard catalog: enabled templates only — disabled/draft never surface.
   * A drawn flow is startable once published, and runs the published version
   * until the next publish. A step list redrawn as a graph keeps running as
   * steps until its graph is first published.
   */
  getStartableTemplates(): WorkflowTemplate[] {
    return this.getTemplates().filter((t) => t.enabled !== false && (!!t.deployment || t.steps.length > 0));
  }

  /** Whether starting this template starts its published process definition rather than a step chain. */
  static runsAsProcess(tpl: WorkflowTemplate | undefined | null): boolean {
    return !!tpl?.graph && !!tpl.deployment;
  }

  /** The graph of a template that runs as a process, or null — for counting and previewing its steps. */
  static publishedGraph(tpl: WorkflowTemplate | undefined | null): FlowGraph | null {
    return tpl?.graph && tpl.deployment ? tpl.graph : null;
  }

  /**
   * The flows this viewer may see and start — enabled templates passing the
   * visibility gate. Administrators see everything; an unreadable viewer sees
   * only unrestricted flows, which is the safe side of a gate.
   */
  visibleStartableTemplates(): Observable<WorkflowTemplate[]> {
    return forkJoin({
      loaded: this.ensureTemplatesLoaded(),
      viewer: this.viewerContext()
    }).pipe(map(({ viewer }) => this.getStartableTemplates().filter((t) => WorkflowService.canSeeTemplate(t, viewer))));
  }

  /** The signed-in person's id, admin flag and group ids — cached per session. */
  viewerContext(): Observable<ViewerContext> {
    if (this.viewer$) {
      return this.viewer$;
    }
    this.viewer$ = forkJoin({
      me: this.coreCall('/people/-me-', 'GET'),
      groups: this.coreCall('/people/-me-/groups', 'GET', { maxItems: 250 }).pipe(catchError(() => of(null)))
    }).pipe(
      map(({ me, groups }) => {
        // capabilities is absent for an administrator on some repository versions
        const caps = me?.entry?.capabilities;
        return {
          id: me?.entry?.id ?? '',
          isAdmin: caps ? !!caps.isAdmin : true,
          groups: new Set<string>((groups?.list?.entries ?? []).map((e: any) => e.entry?.id).filter(Boolean))
        } as ViewerContext;
      }),
      catchError(() => of({ id: '', isAdmin: false, groups: new Set<string>() } as ViewerContext))
    );
    return this.viewer$;
  }

  /** Whether the gate on this template lets the viewer through. */
  static canSeeTemplate(tpl: WorkflowTemplate, viewer: ViewerContext | null | undefined): boolean {
    if (!tpl.restricted) {
      return true;
    }
    if (!viewer) {
      return false;
    }
    if (viewer.isAdmin || viewer.groups.has(WorkflowService.ADMIN_GROUP)) {
      return true;
    }
    if ((tpl.allowedUsers ?? []).some((u) => u.id === viewer.id)) {
      return true;
    }
    const names = new Set<string>();
    for (const g of viewer.groups) {
      names.add(g);
      names.add(g.replace(/^GROUP_/, ''));
    }
    return (tpl.allowedGroups ?? []).some((g) => names.has(g.id));
  }

  /** Flip a template's availability without touching its version history. */
  setTemplateEnabled(id: string, enabled: boolean): Observable<void> {
    const all = [...this.rawTemplates()];
    const i = all.findIndex((t) => t.id === id);
    if (i < 0) {
      return of(undefined);
    }
    all[i] = { ...all[i], enabled };
    return this.persistTemplates(all);
  }

  deleteTemplate(id: string): Observable<void> {
    return this.persistTemplates(this.rawTemplates().filter((t) => t.id !== id));
  }

  // ---- shared catalogue storage (Data Dictionary/ACA) ------------------------

  private viewer$: Observable<ViewerContext> | null = null;
  private tplCache: WorkflowTemplate[] | null = null;

  private static readonly ADMIN_GROUP = 'GROUP_ALFRESCO_ADMINISTRATORS';
  private static readonly SHARED_FOLDER_PATH = 'Data Dictionary/ACA';
  private static readonly SHARED_FILE_NAME = 'workflow-templates.json';

  private nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  private readSharedTemplates(): Observable<WorkflowTemplate[] | null> {
    return this.sharedNodeId(false).pipe(
      switchMap((nodeId) => (nodeId ? from(this.nodes().getNodeContent(nodeId) as Promise<any>) : of(null))),
      switchMap((content: any) => {
        if (!content) {
          return of(null);
        }
        if (typeof content === 'string') {
          return of(content);
        }
        return content instanceof Blob ? from(content.text()) : of(null);
      }),
      map((text) => {
        if (!text) {
          return null;
        }
        try {
          const parsed = JSON.parse(text) as { templates?: WorkflowTemplate[] };
          return Array.isArray(parsed?.templates) ? parsed.templates : null;
        } catch {
          // a hand-edited or truncated document must not empty the catalogue
          return null;
        }
      })
    );
  }

  private writeSharedTemplates(templates: WorkflowTemplate[]): Observable<void> {
    return this.sharedNodeId(true).pipe(
      switchMap((nodeId) =>
        nodeId
          ? from(this.nodes().updateNodeContent(nodeId, JSON.stringify({ templates })) as Promise<any>).pipe(map(() => undefined))
          : of(undefined)
      )
    );
  }

  /**
   * The document's node id, creating the folder and the document on the way
   * when `create` is set — which only ever happens on an administrator's save.
   */
  private sharedNodeId(create: boolean): Observable<string | null> {
    const nodes = this.nodes();

    // listed once for every settings document — see AcaConfigFolderService
    return this.configFolder.find(WorkflowService.SHARED_FILE_NAME).pipe(
      catchError(() => {
        if (!create) {
          return of(null);
        }

        return from(nodes.createNode('-root-', { name: 'ACA', nodeType: 'cm:folder', relativePath: 'Data Dictionary' } as any) as Promise<any>).pipe(
          catchError(() => of(null)),
          switchMap(() =>
            from(
              nodes.createNode('-root-', {
                name: WorkflowService.SHARED_FILE_NAME,
                nodeType: 'cm:content',
                relativePath: WorkflowService.SHARED_FOLDER_PATH
              } as any) as Promise<any>
            )
          ),
          map((entry: any) => entry?.entry?.id as string),
          tap(() => this.configFolder.forget())
        );
      })
    );
  }

  // ---- template instances, kept by the jobs sidecar -------------------------

  private get instancesUrl(): string {
    return `${this.appConfig.get<string>('jobs.baseUrl', '/jobs-api')}/api/workflow/instances`;
  }

  /** The sidecar asks Alfresco about the caller; a ticket travels in the ROLE_TICKET form. */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }

  /** The flow one of whose steps is this process; null when it is not a template flow. */
  instanceForProcess(processId: string): Observable<TemplateInstance | null> {
    return this.http
      .get<TemplateInstance>(`${this.instancesUrl}/by-process/${encodeURIComponent(processId)}`, { headers: this.credentials() })
      .pipe(catchError(() => of(null)));
  }

  /** The flows this person started — every flow, for an administrator. */
  listInstances(): Observable<TemplateInstance[]> {
    return this.migrateLocalInstances().pipe(
      switchMap(() => this.http.get<TemplateInstance[]>(this.instancesUrl, { headers: this.credentials() })),
      catchError(() => of([] as TemplateInstance[]))
    );
  }

  createInstance(inst: TemplateInstance): Observable<TemplateInstance> {
    return this.http.post<TemplateInstance>(this.instancesUrl, inst, { headers: this.credentials() });
  }

  updateInstance(inst: TemplateInstance): Observable<TemplateInstance> {
    return this.http.put<TemplateInstance>(`${this.instancesUrl}/${encodeURIComponent(inst.id)}`, inst, { headers: this.credentials() });
  }

  /**
   * Flows started before the record moved to the server were kept in this
   * browser only. Hand each over once — the server accepts only those whose
   * process this person can see — then forget the local copy.
   */
  private migrateLocalInstances(): Observable<void> {
    let local: TemplateInstance[] = [];
    try {
      local = JSON.parse(localStorage.getItem(INSTANCES_KEY) || '[]');
    } catch {
      local = [];
    }
    if (!local.length) {
      return of(undefined);
    }
    return forkJoin(local.map((inst) => this.createInstance(inst).pipe(catchError(() => of(null))))).pipe(
      map(() => {
        localStorage.removeItem(INSTANCES_KEY);
        return undefined;
      })
    );
  }

  /** Start one step of a template as a real Activiti task and return the process. */
  startStep(step: WorkflowStep, description: string, items: string[] = [], extra: Record<string, any> = {}): Observable<WorkflowProcess> {
    const isGroup = step.assigneeType === 'group';
    const key = step.type === 'review' ? (isGroup ? 'activitiParallelGroupReview' : 'activitiReview') : 'activitiAdhoc';
    const variables: Record<string, any> = {
      bpm_workflowDescription: description,
      bpm_priority: extra.bpm_priority ?? 2,
      ...extra
    };
    if (isGroup) {
      variables.bpm_groupAssignee = `GROUP_${step.assignee.replace(/^GROUP_/, '')}`;
    } else {
      variables.bpm_assignee = step.assignee;
    }
    return this.startProcess(key, variables, items);
  }

  /**
   * Starts a template on documents already in the repository: a published
   * drawn flow as its own process; a step list from the first step that
   * applies to `vars`, recorded so the later steps follow it. Fails with
   * {@link NO_ASSIGNEE} before starting anything when that step has nobody.
   */
  launchTemplate(
    tpl: WorkflowTemplate,
    description: string,
    nodeIds: string[],
    vars: Record<string, any> = {},
    extra: Record<string, any> = {}
  ): Observable<LaunchedFlow> {
    const text = `${tpl.name} — ${description}`;
    const deployment = WorkflowService.runsAsProcess(tpl) ? tpl.deployment : undefined;
    if (deployment) {
      return this.startProcess(deployment.key, { bpm_workflowDescription: text, ...extra }, nodeIds).pipe(
        map((process) => ({ process, recorded: true }))
      );
    }
    const values = { ...vars, ...extra };
    const first = this.nextRunnableStep(tpl, 0, values);
    const step = first >= 0 ? tpl.steps[first] : undefined;
    if (!step?.assignee) {
      return throwError(() => new Error(NO_ASSIGNEE));
    }
    return this.startStep(step, text, nodeIds, extra).pipe(
      switchMap((process) => this.processTasks(process.id).pipe(map((tasks) => ({ process, taskId: tasks[0]?.id })))),
      switchMap(({ process, taskId }) =>
        this.createInstance({
          id: `inst-${Date.now()}`,
          templateId: tpl.id,
          templateName: tpl.name,
          templateVersion: tpl.version ?? 1,
          nodeIds,
          currentStep: first,
          status: 'running',
          taskId,
          processId: process.id,
          startedAt: new Date().toISOString(),
          history: [],
          vars: values
        }).pipe(
          map(() => ({ process, recorded: true })),
          // the first step runs either way; the caller says the rest will not follow
          catchError(() => of({ process, recorded: false }))
        )
      )
    );
  }

  // ---- step routing ---------------------------------------------------------

  /**
   * Whether a step may run against the values an instance carries. A step
   * without a condition (or an unreadable one) always runs — conditions are
   * additive and must never silently freeze older templates.
   */
  stepRunnable(step: WorkflowStep | undefined, vars: Record<string, any> | undefined): boolean {
    if (!step) {
      return false;
    }
    const c = step.condition;
    if (!c?.field?.trim()) {
      return true;
    }
    const raw = vars?.[c.field.trim()];
    const text = raw === null || raw === undefined ? '' : String(raw);
    const target = c.value ?? '';
    const num = Number(text);
    const targetNum = Number(target);
    const numeric = text.trim() !== '' && !Number.isNaN(num) && target.trim() !== '' && !Number.isNaN(targetNum);
    switch (c.op) {
      case 'eq':
        return text === target;
      case 'ne':
        return text !== target;
      case 'gt':
        return numeric && num > targetNum;
      case 'gte':
        return numeric && num >= targetNum;
      case 'lt':
        return numeric && num < targetNum;
      case 'lte':
        return numeric && num <= targetNum;
      case 'contains':
        return text.toLowerCase().includes(target.toLowerCase());
      case 'empty':
        return text === '';
      case 'notEmpty':
        return text !== '';
      default:
        return true;
    }
  }

  /** First step at or after `startAt` whose condition passes — -1 when the flow is done. */
  nextRunnableStep(tpl: WorkflowTemplate | undefined, startAt: number, vars?: Record<string, any>): number {
    if (!tpl) {
      return -1;
    }
    for (let i = Math.max(0, startAt); i < tpl.steps.length; i++) {
      if (this.stepRunnable(tpl.steps[i], vars)) {
        return i;
      }
    }
    return -1;
  }
}
